import { useEffect, useRef } from 'react';

import last from 'lodash/last';
import uniq from 'lodash/uniq';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type QueryParamType = 'string' | 'number' | 'boolean' | 'date';

type ValueMapping = {
  string: string[];
  number: number[];
  boolean: boolean[];
  date: Date[];
};

export type ValueFromType<T extends QueryParamType> = ValueMapping[T];

export type QueryParamConfig<T extends QueryParamType> = {
  pageKey: string;
  paramKey: string;
  type: T;
  defaultValue?: ValueFromType<T>;
  allIfNoneValue?: ValueFromType<T>[number];
};

type QueryParamStoreState = {
  pageParams: Record<string, Record<string, any>>;
  setParam: <T extends QueryParamType>(
    pageKey: string,
    paramKey: string,
    value: ValueFromType<T>
  ) => void;
  resetParam: <T extends QueryParamType>(
    pageKey: string,
    paramKey: string,
    defaultValue: ValueFromType<T>
  ) => void;
  resetAll: () => void;
};

export const useQueryParamStore = create<QueryParamStoreState>()(
  persist(
    (set) => ({
      pageParams: {},

      setParam: (pageKey, paramKey, value) =>
        set((state) => ({
          pageParams: {
            ...state.pageParams,
            [pageKey]: {
              ...(state.pageParams[pageKey] || {}),
              [paramKey]: value,
            },
          },
        })),

      resetParam: (pageKey, paramKey, defaultValue) =>
        set((state) => ({
          pageParams: {
            ...state.pageParams,
            [pageKey]: {
              ...(state.pageParams[pageKey] || {}),
              [paramKey]: defaultValue,
            },
          },
        })),
      resetAll: () => set({ pageParams: {} }),
    }),
    { name: 'filters-store-multi' }
  )
);

export function usePersistedParams<T extends QueryParamType>(
  config: QueryParamConfig<T>
): [
  ValueFromType<T>,
  (v: ValueFromType<T>) => void,
  { reset: () => void; initialValue: ValueFromType<T> }
] {
  const { pageKey, paramKey, type, defaultValue, allIfNoneValue } = config;

  const value = useQueryParamStore(
    (state) => state.pageParams[pageKey]?.[paramKey]
  );

  const initialValue = useRef(value ?? ([] as ValueFromType<T>)).current;

  const setValue = (v: ValueFromType<T>) => {
    let newValue: ValueFromType<T> =
      type === 'date' ? v : (uniq(v as any) as ValueFromType<T>);

    if (allIfNoneValue) {
      if (last(newValue as any) === allIfNoneValue || newValue.length === 0) {
        newValue = [allIfNoneValue] as ValueFromType<T>;
      } else {
        newValue = newValue.filter(
          (x) => x !== allIfNoneValue
        ) as ValueFromType<T>;
      }
    }

    const serialized = newValue
      .map((value) => {
        if (type === 'date') {
          if (value == null) {
            return '';
          }
          return (value as Date).toISOString();
        }
        return String(value);
      })
      .join(',');

    const params = new URLSearchParams(window.location.search);
    if (!serialized) params.delete(paramKey);
    else params.set(paramKey, serialized);
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${params}`
    );

    useQueryParamStore.getState().setParam(pageKey, paramKey, newValue);
  };

  const reset = () => {
    const def = defaultValue ?? ([] as ValueFromType<T>);
    useQueryParamStore.getState().resetParam(pageKey, paramKey, def);
    setValue(def);
  };

  useEffect(() => {
    if (value === undefined) setValue(config?.defaultValue as any);
    // eslint-disable-next-line
  }, [value]);

  return [value ?? initialValue, setValue, { reset, initialValue }];
}
